import { logEvent } from '@/components/analytics/GoogleAnalytics';
import { GradientMesh } from '@/components/GradientMesh';
import HomeSection from '@/components/HomeSection';
import Link from '@/components/Link';
import { Pane } from '@/components/pane';
import { ReactComponent as DiagonalBottom } from '@/components/renderables/diagonal-bottom.svg';
import { ReactComponent as DiagonalTop } from '@/components/renderables/diagonal-top.svg';
import SectionContainer from '@/components/SectionContainer';
import { PageSEO } from '@/components/SEO';
import { GRADIENT_MESH } from '@/data/colorGradient';
import { ReactComponent as ExafunctionVisual } from '@/data/ef_visual.svg';
import { integrationConfig } from '@/data/integrationConfig';
import { modelRegistrationConfig } from '@/data/modelRegistrationConfig';
import siteMetadata from '@/data/siteMetadata';
import { useTailwindConfig } from '@/hooks/useTailwindConfig';
import { getAllFilesFrontMatter } from '@/lib/mdx';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

const MAX_DISPLAY = 5;

export async function getStaticProps() {
  const posts = await getAllFilesFrontMatter('blog');

  return { props: { posts } };
}

export default function Home({ posts }) {
  const router = useRouter();
  const {
    theme: {
      colors: { secondary },
    },
  } = useTailwindConfig();

  const handleDemoButtonClick = useCallback(() => {
    router.push('/contact');
    logEvent('home_page_demo_click', 'button_click', 'demo_button');
  }, [router]);

  const handleGetStartedButtonClick = useCallback(() => {
    router.push('/contact');
    logEvent('home_page_get_started_click', 'button_click', 'get_started_button');
  }, [router]);

  return (
    <>
      <PageSEO title={siteMetadata.title} description={siteMetadata.description} />
      <div className="relative flex flex-col items-center">
        <div className="relative flex h-screen w-screen items-center justify-center overflow-hidden bg-primary-900">
          <div className="relative flex flex-col items-center">
            <div className="relative h-screen w-screen overflow-hidden">
              <GradientMesh colorPalette={GRADIENT_MESH} opacity={0.5} />
            </div>

            <div className="absolute top-0 mt-16 flex h-full w-full flex-col items-center justify-center">
              <div className="container mx-auto flex -translate-y-[12.5%] flex-col items-center py-6 sm:py-16 md:-translate-y-1/4">
                <div className="mb-5 flex w-11/12 flex-col items-center justify-center sm:mb-10 sm:w-4/5">
                  <Link
                    href={siteMetadata.docsUrl}
                    className="announcement m-4 mb-16 max-w-sm rounded-full bg-secondary-900 py-2 px-6 text-center text-base font-medium sm:text-lg md:text-lg"
                  >
                    We've publicly released our docs
                  </Link>
                  <h1 className="text-center text-2xl font-normal leading-7 text-white dark:text-gray-50 sm:text-3xl md:text-4xl md:leading-10 lg:text-5xl xl:text-6xl">
                    Efficient deep learning at{' '}
                    <span
                      className="text-primary-900"
                      style={{
                        WebkitTextStrokeWidth: 1,
                        WebkitTextStrokeColor: secondary['900'],
                      }}
                    >
                      scale.
                    </span>
                  </h1>
                  <p className="mt-5 text-center text-lg font-normal text-white dark:text-gray-200 sm:mt-10 sm:text-lg lg:w-11/12">
                    Exafunction optimizes your deep learning inference workload, delivering up to a
                    10x improvement in resource utilization and cost. Focus on building your deep
                    learning application, not on managing clusters and fine-tuning performance.
                  </p>
                </div>
                <div className="flex items-center justify-center">
                  <button
                    className="rounded border-2 border-secondary-900 bg-secondary-900 px-4 py-2 text-lg text-black transition duration-150 ease-in-out  hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-secondary-700 focus:ring-offset-2 sm:px-10 sm:py-4 lg:text-xl lg:font-bold"
                    onClick={handleGetStartedButtonClick}
                  >
                    Get Started
                  </button>
                  <button
                    className="ml-4 rounded border-2 border-secondary-900 px-4 py-2 text-lg text-secondary-900 transition duration-150  ease-in-out hover:border-secondary-600 hover:text-secondary-600 focus:outline-none focus:ring-2 focus:ring-secondary-700 focus:ring-offset-2 sm:px-10 sm:py-4 lg:text-xl lg:font-bold"
                    onClick={handleDemoButtonClick}
                  >
                    Demo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="grid w-full grid-cols-1 gap-16 px-6 pt-24 md:grid-cols-2">
        <div className="col-span-1">
          <HomeSection
            title="Without Exafunction"
            description="In most deep learning applications, CPU, I/O, and network bottlenecks lead to poor utilization of GPU hardware."
            h3={true}
            extraContainerClassNames="text-center"
          />
          <div className="relative block h-[60vh] max-h-[900px] w-full md:hidden">
            <video
              alt="gpu-efficiency-animation"
              width="100%"
              height="100%"
              className="h-full w-full object-contain"
              autoPlay
              muted
              loop
            >
              <source src="/static/animations/vertical-inefficient.mp4" type="video/mp4" />
            </video>
          </div>
        </div>

        <div className="col-span-1">
          <HomeSection
            title="With Exafunction"
            description="Exafunction moves any GPU code to highly utilized remote resources, even spot instances. Your core logic remains on inexpensive CPU instances."
            h3={true}
            extraContainerClassNames="text-center"
          />
          <div className="relative block h-[60vh] max-h-[900px] w-full md:hidden">
            <video
              alt="gpu-efficiency-animation"
              width="100%"
              height="100%"
              className="h-full w-full object-contain"
              autoPlay
              muted
              loop
            >
              <source src="/static/animations/vertical-efficient.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
      <div className="hidden w-full grid-cols-2 gap-16 px-6 md:grid">
        <div className="col-span-1">
          <div className="relative block h-[60vh] max-h-[900px] w-full">
            <video
              alt="gpu-efficiency-animation"
              width="100%"
              height="100%"
              className="h-full w-full object-contain"
              autoPlay
              muted
              loop
            >
              <source src="/static/animations/vertical-inefficient.mp4" type="video/mp4" />
            </video>
          </div>
        </div>

        <div className="col-span-1">
          <div className="relative block h-[60vh] max-h-[900px] w-full">
            <video
              alt="gpu-efficiency-animation"
              width="100%"
              height="100%"
              className="h-full w-full object-contain"
              autoPlay
              muted
              loop
            >
              <source src="/static/animations/vertical-efficient.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>

      <div className="relative flex flex-col items-center">
        <div className="relative w-screen overflow-hidden">
          <DiagonalTop />
          <div className="w-full bg-secondary-200 py-16">
            <SectionContainer>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="col-span-1">
                  <HomeSection
                    title="Trusted by the most demanding applications"
                    description="Exafunction is battle-tested on applications like large-scale autonomous vehicle
            simulation. These workloads have complex custom models, require
            numerical reproducibility, and use thousands of GPUs concurrently."
                    extraContainerClassNames="px-6"
                  />
                </div>
                <div className="col-span-1 flex items-center justify-center p-8">
                  <ExafunctionVisual />
                </div>
              </div>
            </SectionContainer>
          </div>
          <DiagonalBottom />
        </div>
      </div>

      <HomeSection title="How it Works" extraContainerClassNames="px-6">
        <div className="my-16 flex w-full flex-col items-center md:flex-row">
          <div className="w-full pr-0 text-left md:pr-8 lg:w-2/5 lg:pr-16 xl:pr-28">
            <h3 className="text-xl font-semibold leading-7 text-gray-900 dark:text-gray-100 sm:text-xl md:text-2xl md:leading-10 lg:text-3xl xl:text-4xl">
              Register Any Model
            </h3>
            <p className="mt-5 mb-12 text-lg font-normal text-gray-700 dark:text-gray-200 sm:mt-10 sm:text-lg">
              Exafunction supports models from major deep learning frameworks and inference
              runtimes. Models and dependencies like custom operators are versioned so you can
              always be confident you&rsquo;re getting the right results.
            </p>
          </div>
          <div className="w-full lg:w-3/5">
            <Pane items={modelRegistrationConfig} />
          </div>
        </div>

        <div className="my-16 flex w-full flex-col-reverse items-center md:flex-row">
          <div className="w-full lg:w-3/5">
            <Pane items={integrationConfig} />
          </div>
          <div className="w-full pl-0 text-left md:pl-8 lg:w-2/5 lg:pl-16 xl:pl-28">
            <h3 className="text-xl font-semibold leading-7 text-gray-900 dark:text-gray-100 sm:text-xl md:text-2xl md:leading-10 lg:text-3xl xl:text-4xl">
              Integrate Your Application
            </h3>
            <p className="mt-5 mb-12 text-lg font-normal text-gray-700 dark:text-gray-200 sm:mt-10 sm:text-lg">
              Integration is as simple as replacing your framework&rsquo;s model inference functions
              with a few lines of code.
            </p>
          </div>
        </div>
      </HomeSection>

      {posts.length > MAX_DISPLAY && (
        <div className="flex justify-end text-base font-medium leading-6">
          <Link
            href="/blog"
            className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
            aria-label="all posts"
          >
            All Posts &rarr;
          </Link>
        </div>
      )}
    </>
  );
}
